import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Building2, User, Phone, Mail, MapPin, Calendar, Store } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CompanySearch } from '@/components/ui/company-search';
import { ContactPersonSearch } from '@/components/ui/contact-person-search';
import { AddressSelector } from '@/components/ui/address-selector';
import CountrySelector from '@/components/ui/country-selector';
import PhoneNumberInput from '@/components/ui/phone-number-input';
import { DealerSurveyData } from '@/api/types';
import { useRaynetUsers } from '@/hooks/useRaynetUsers';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { raynetClient } from '@/api/raynetClient';
import { isValidEmail, isValidContactName } from '@/utils/validation';

interface FormStepOneProps {
  formData: DealerSurveyData;
  updateFormData: (updates: Partial<DealerSurveyData>) => void;
  readOnly?: boolean;
}

const FormStepOne: React.FC<FormStepOneProps> = ({ formData, updateFormData, readOnly = false }) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  useRaynetUsers();
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const [companyAddresses, setCompanyAddresses] = useState<any[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<string>('CZ');
  const contactNameInputRef = useRef<HTMLInputElement>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Auto-set sales rep when user data is available
  useEffect(() => {
    if (user?.full_name && !formData.salesRep) {
      updateFormData({ salesRep: user.full_name });
    }
  }, [user, formData.salesRep, updateFormData]);

  // Auto-set today's date if not set
  useEffect(() => {
    if (!formData.visitDate) {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      updateFormData({ visitDate: today });
    }
  }, [formData.visitDate, updateFormData]);

  const handleChange = (field: keyof DealerSurveyData, value: string) => {
    const updates: Partial<DealerSurveyData> = { [field]: value };
    
    // Pokud uživatel mění název firmy a má vybranou firmu z Raynetu,
    // ale nový název už není stejný jako vybraná firma, vymažeme výběr
    if (field === 'companyName' && formData.selectedRaynetCompany) {
      if (value !== formData.selectedRaynetCompany.name) {
        updates.selectedRaynetCompany = undefined;
        updates.selectedRaynetContactPerson = undefined;
        updates.selectedRaynetAddress = undefined;
        updates.contactName = '';
        updates.contactPhone = '';
        updates.contactEmail = '';
        updates.showroomStreet = '';
        updates.showroomCity = '';
        updates.showroomZipCode = '';
        updates.showroomCountry = '';
        setShowAddressSelector(false);
        setCompanyAddresses([]);
        console.log('Vymazávám vybranou firmu z Raynetu - uživatel změnil název');
      }
    }

    // Pokud uživatel mění název firmy a má vybranou firmu z ARES,
    // ale nový název už není stejný jako vybraná firma, vymažeme výběr
    if (field === 'companyName' && formData.selectedAresCompany) {
      if (value !== formData.selectedAresCompany.name) {
        updates.selectedAresCompany = undefined;
        console.log('Vymazávám vybranou firmu z ARES - uživatel změnil název');
      }
    }
    
    // Pokud uživatel mění jméno kontaktu a má vybranou osobu z Raynetu
    if (field === 'contactName' && formData.selectedRaynetContactPerson) {
      if (value.trim() === '' || value !== formData.selectedRaynetContactPerson.fullName) {
        updates.selectedRaynetContactPerson = undefined;
        updates.contactPhone = '';
        updates.contactEmail = '';
        console.log('Vymazávám vybranou kontaktní osobu z Raynetu včetně kontaktních údajů - uživatel změnil/vymazal jméno');
      }
    }
    
    // Pokud uživatel mění kontaktní údaje a má vybranou osobu z Raynetu,
    // ale údaje už nejsou stejné, vymažeme výběr (zachováme změny)
    if ((field === 'contactPhone' || field === 'contactEmail') && formData.selectedRaynetContactPerson) {
      if (field === 'contactPhone' && value !== (formData.selectedRaynetContactPerson.phone || '')) {
        updates.selectedRaynetContactPerson = undefined;
        console.log('Vymazávám vybranou kontaktní osobu z Raynetu - uživatel změnil telefon');
      }
      if (field === 'contactEmail' && value !== (formData.selectedRaynetContactPerson.email || '')) {
        updates.selectedRaynetContactPerson = undefined;
        console.log('Vymazávám vybranou kontaktní osobu z Raynetu - uživatel změnil email');
      }
    }

    // Pokud uživatel mění adresní údaje a má vybranou adresu z Raynetu,
    // ale údaje už nejsou stejné, vymažeme výběr (zachováme změny)
    if ((field === 'showroomStreet' || field === 'showroomCity' || field === 'showroomZipCode' || field === 'showroomCountry') && formData.selectedRaynetAddress) {
      let currentValue = '';
      if (field === 'showroomStreet') currentValue = formData.selectedRaynetAddress.street || '';
      if (field === 'showroomCity') currentValue = formData.selectedRaynetAddress.city || '';
      if (field === 'showroomZipCode') currentValue = formData.selectedRaynetAddress.zipCode || '';
      if (field === 'showroomCountry') currentValue = formData.selectedRaynetAddress.country || '';
      
      if (value !== currentValue) {
        updates.selectedRaynetAddress = undefined;
        console.log('Vymazávám vybranou adresu z Raynetu - uživatel změnil', field);
      }
    }
    
    updateFormData(updates);
  };

  const handleCompanySelect = async (company: any) => {
    console.log('Vybraná firma z Raynetu:', company);
    
    // Autofill available data from selected company
    const updates: Partial<DealerSurveyData> = {
      companyName: company.name,
      ico: company.ico,
      selectedRaynetCompany: company,
      selectedAresCompany: undefined,
      selectedRaynetContactPerson: undefined,
      selectedRaynetAddress: undefined,
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      showroomStreet: '',
      showroomCity: '',
      showroomZipCode: '',
      showroomCountry: '',
      // Zajistit že salesRep je vždy vyplněný pro Raynet export
      salesRep: formData.salesRep || user?.full_name || '',
    };
    
    updateFormData(updates);
    
    // Load company detail with addresses
    setIsLoadingAddresses(true);
    try {
      const companyDetail = await raynetClient.getCompanyDetail(company.id);
      console.log('Detail firmy načten:', companyDetail);
      
      if (companyDetail.addresses && companyDetail.addresses.length > 0) {
        setCompanyAddresses(companyDetail.addresses);
        
        // If only one address, auto-select it
        if (companyDetail.addresses.length === 1) {
          handleAddressSelect(companyDetail.addresses[0]);
        } else {
          // Show address selector for multiple addresses
          setShowAddressSelector(true);
        }
      } else {
        console.log('Firma nemá žádné adresy');
        setCompanyAddresses([]);
      }
    } catch (error) {
      console.error('Chyba při načítání detailu firmy:', error);
      setCompanyAddresses([]);
    } finally {
      setIsLoadingAddresses(false);
    }
    
    // Focus na kontaktní osobu po výběru firmy
    setTimeout(() => {
      contactNameInputRef.current?.focus();
    }, 100);
  };

  const handleAresCompanySelect = (company: any) => {
    console.log('Vybraná firma z ARES:', company);
    
    const updates: Partial<DealerSurveyData> = {
      companyName: company.name,
      ico: company.ico,
      selectedAresCompany: company,
      selectedRaynetCompany: undefined,
      selectedRaynetContactPerson: undefined,
      selectedRaynetAddress: undefined,
      // Kontaktní údaje se nevyplní - uživatel je musí doplnit ručně
      // ale zachováme je pokud už jsou vyplněné
      contactName: formData.contactName || '',
      contactPhone: formData.contactPhone || '',
      contactEmail: formData.contactEmail || '',
      // Zajistit že salesRep je vždy vyplněný pro Raynet export
      salesRep: formData.salesRep || user?.full_name || '',
    };

    // Automatické vyplnění adresy z ARES dat
    if (company.address) {
      // ARES je český systém, takže země bude vždy CZ
      updates.showroomStreet = company.address.street;
      updates.showroomCity = company.address.city;
      updates.showroomZipCode = company.address.zipCode;
      updates.showroomCountry = 'CZ'; // ARES obsahuje pouze české firmy
      console.log('Adresa vyplněna z ARES:', company.address);
    } else {
      updates.showroomStreet = '';
      updates.showroomCity = '';
      updates.showroomZipCode = '';
      updates.showroomCountry = 'CZ'; // Výchozí pro ARES firmy
    }
    
    updateFormData(updates);
    
    // Hide any open address selectors
    setShowAddressSelector(false);
    setCompanyAddresses([]);
    
    // Focus na kontaktní osobu po výběru ARES firmy
    setTimeout(() => {
      contactNameInputRef.current?.focus();
    }, 100);
  };

  const handleContactPersonSelect = (contact: any) => {
    if (contact) {
      updateFormData({
        contactName: contact.fullName,
        contactPhone: contact.phone || '',
        contactEmail: contact.email || '',
        selectedRaynetContactPerson: contact,
      });
    }
  };

  const handleAddressSelect = (address: any) => {
    console.log('Vybraná adresa z Raynetu:', address);
    
    // Převést název země na kód země (jednoduchá implementace)
    const getCountryCode = (countryName: string): string => {
      const countryMap: Record<string, string> = {
        'Česká republika': 'CZ',
        'Czech Republic': 'CZ',
        'Slovensko': 'SK',
        'Slovakia': 'SK',
        'Polsko': 'PL',
        'Poland': 'PL',
        'Německo': 'DE',
        'Germany': 'DE',
        'Rakousko': 'AT',
        'Austria': 'AT',
        'Maďarsko': 'HU',
        'Hungary': 'HU',
        // Můžeme přidat více podle potřeby
      };
      
      return countryMap[countryName] || countryName; // Fallback na původní hodnotu
    };
    
    const updates: Partial<DealerSurveyData> = {
      showroomStreet: address.street,
      showroomCity: address.city,
      showroomZipCode: address.zipCode,
      showroomCountry: getCountryCode(address.country),
      selectedRaynetAddress: address,
    };
    
    updateFormData(updates);
    setShowAddressSelector(false);
  };

  const handleAddressSelectorCancel = () => {
    setShowAddressSelector(false);
    setCompanyAddresses([]);
  };

  const handleCountryChange = (code: string) => {
    updateFormData({ showroomCountry: code });
    
    // Clear selected Raynet address if country changes
    if (formData.selectedRaynetAddress) {
      updateFormData({ selectedRaynetAddress: undefined });
      console.log('Vymazávám vybranou adresu z Raynetu - uživatel změnil zemi');
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  const handleNoShowroomChange = (checked: boolean) => {
    // Pouze změníme příznak, adresa zůstává povinná pro Raynet
    updateFormData({
      hasNoShowroom: checked
    });
  };

  // Adresní pole jsou vždy povinná kvůli vytvoření klienta v Raynetu
  const requiredFields: (keyof DealerSurveyData)[] = ['companyName', 'contactName', 'contactPhone', 'contactEmail', 'showroomStreet', 'showroomCity', 'showroomZipCode', 'showroomCountry', 'visitDate'];
  
  const isFieldEmpty = (field: keyof DealerSurveyData) => {
    const value = formData[field];
    if (value === null || value === undefined) return true;
    const stringValue = String(value).trim();
    return stringValue === '';
  };

  const getContactNameError = (): string | null => {
    if (!touchedFields.has('contactName')) return null;
    
    if (!formData.contactName || formData.contactName.trim() === '') {
      return t('validation.required');
    }
    if (!isValidContactName(formData.contactName)) {
      return t('form_step_1.contact_name_full_required' as any);
    }
    return null;
  };

  const getContactEmailError = (): string | null => {
    if (!touchedFields.has('contactEmail')) return null;
    
    if (!formData.contactEmail || formData.contactEmail.trim() === '') {
      return t('validation.required');
    }
    if (!isValidEmail(formData.contactEmail)) {
      return 'Zadejte platnou e-mailovou adresu';
    }
    return null;
  };

  return (
    <div className={`space-y-6 ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
      {/* Alert for required fields */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="text-amber-800 text-sm font-medium">
                {t('validation.required_fields')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form fields */}
      <div className="space-y-6">
        {/* Company Name & Contact Name */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label 
              htmlFor="companyName" 
              className="text-slate-700 font-medium text-sm flex items-center gap-2"
            >
              <Building2 className="h-4 w-4" />
              {t('form_step_1.company_name')}
              <span className="text-red-500">*</span>
            </Label>
            <CompanySearch
              value={formData.companyName}
              onChange={(value) => handleChange('companyName', value)}
              onCompanySelect={handleCompanySelect}
              onAresCompanySelect={formData.showroomCountry === 'CZ' ? handleAresCompanySelect : undefined}
              placeholder={t('form_step_1.company_placeholder')}
              country={formData.showroomCountry}
              className={`transition-all duration-200 ${
                isFieldEmpty('companyName') 
                  ? '' 
                  : 'company-selected'
              }`}
            />
            {formData.selectedRaynetCompany && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {t('form_step_1.company_found_raynet')}
              </p>
            )}
            {formData.selectedAresCompany && (
              <p className="text-xs text-orange-600 flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {t('form_step_1.company_found_ares')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label 
              htmlFor="contactName" 
              className="text-slate-700 font-medium text-sm flex items-center gap-2"
            >
              <User className="h-4 w-4" />
              {t('form_step_1.contact_name')}
              <span className="text-red-500">*</span>
            </Label>
            {formData.selectedRaynetCompany ? (
              <ContactPersonSearch
                companyId={formData.selectedRaynetCompany.id}
                value={formData.contactName}
                onChange={(value) => handleChange('contactName', value)}
                onContactSelect={handleContactPersonSelect}
                placeholder={t('form_step_1.contact_name_placeholder')}
                className={`transition-all duration-200 ${
                  getContactNameError() 
                    ? 'border-red-300' 
                    : isFieldEmpty('contactName') 
                      ? 'border-slate-300' 
                      : 'border-green-300'
                }`}
              />
            ) : (
              <Input
                ref={contactNameInputRef}
                id="contactName"
                value={formData.contactName}
                onChange={(e) => handleChange('contactName', e.target.value)}
                onBlur={() => handleFieldBlur('contactName')}
                placeholder={t('form_step_1.contact_name_placeholder')}
                className={`h-12 transition-all duration-200 ${
                  getContactNameError() 
                    ? 'border-red-300' 
                    : isFieldEmpty('contactName') 
                      ? 'border-slate-300' 
                      : 'border-green-300'
                }`}
              />
            )}
            {formData.selectedRaynetContactPerson && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <User className="h-3 w-3" />
                {t('ui.contact_search.contact_from_raynet')}
              </p>
            )}
            {getContactNameError() && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {getContactNameError()}
              </p>
            )}
          </div>
        </div>

        {/* Phone & Email */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label 
              htmlFor="contactPhone" 
              className="text-slate-700 font-medium text-sm flex items-center gap-2"
            >
              <Phone className="h-4 w-4" />
              {t('form_step_1.contact_phone')}
              <span className="text-red-500">*</span>
            </Label>
            <PhoneNumberInput
              value={formData.contactPhone}
              onChange={(value) => handleChange('contactPhone', value)}
              onCountryChange={(countryCode) => setPhoneCountry(countryCode)}
              placeholder={t('form_step_1.contact_phone_placeholder')}
              defaultCountry={phoneCountry}
              disabled={readOnly}
            />
            {formData.selectedRaynetCompany && formData.contactPhone && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {t('ui.company_search.phone_from_raynet')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label 
              htmlFor="contactEmail" 
              className="text-slate-700 font-medium text-sm flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              {t('form_step_1.contact_email')}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contactEmail"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleChange('contactEmail', e.target.value)}
              onBlur={() => handleFieldBlur('contactEmail')}
              placeholder={t('form_step_1.contact_email_placeholder')}
              className={`h-12 transition-all duration-200 ${
                getContactEmailError() 
                  ? 'border-red-300' 
                  : isFieldEmpty('contactEmail') 
                    ? 'border-slate-300' 
                    : 'border-green-300'
              }`}
            />
            {getContactEmailError() && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {getContactEmailError()}
              </p>
            )}
          </div>
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-slate-600" />
              <h3 className="text-base font-semibold text-slate-800">
                {t('form_step_1.showroom_address')}
              </h3>
            </div>
            
            {/* Checkbox Nemá showroom */}
            <div className="flex items-center space-x-2 px-4 py-2 bg-amber-50 border-2 border-amber-300 rounded-lg hover:bg-amber-100 transition-colors">
              <Checkbox
                id="hasNoShowroom"
                checked={formData.hasNoShowroom || false}
                onCheckedChange={handleNoShowroomChange}
                className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                disabled={readOnly}
              />
              <Label
                htmlFor="hasNoShowroom"
                className="text-sm font-semibold text-amber-800 cursor-pointer flex items-center gap-2"
              >
                <Store className="h-4 w-4" />
                Nemá showroom
              </Label>
            </div>
          </div>

          {formData.hasNoShowroom && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-700">
                <strong>Poznámka:</strong> I když společnost nemá showroom, adresa sídla je povinná pro vytvoření záznamu v systému Raynet.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label 
                htmlFor="showroomStreet" 
                className="text-slate-700 font-medium text-sm"
              >
                {t('form_step_1.showroom_street')}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="showroomStreet"
                value={formData.showroomStreet}
                onChange={(e) => handleChange('showroomStreet', e.target.value)}
                placeholder={formData.hasNoShowroom ? "Adresa sídla společnosti" : t('form_step_1.showroom_street_placeholder')}
                className={`h-12 transition-all duration-200 ${
                  isFieldEmpty('showroomStreet') 
                    ? 'border-slate-300' 
                    : 'border-green-300'
                }`}
              />
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="showroomCity" 
                className="text-slate-700 font-medium text-sm"
              >
                {t('form_step_1.showroom_city')}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="showroomCity"
                value={formData.showroomCity}
                onChange={(e) => handleChange('showroomCity', e.target.value)}
                placeholder={t('form_step_1.showroom_city_placeholder')}
                className={`h-12 transition-all duration-200 ${
                  isFieldEmpty('showroomCity') 
                    ? 'border-slate-300' 
                    : 'border-green-300'
                }`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label 
                htmlFor="showroomZipCode" 
                className="text-slate-700 font-medium text-sm"
              >
                {t('form_step_1.showroom_zip')}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="showroomZipCode"
                value={formData.showroomZipCode}
                onChange={(e) => handleChange('showroomZipCode', e.target.value)}
                placeholder={t('form_step_1.showroom_zip_placeholder')}
                className={`h-12 transition-all duration-200 ${
                  isFieldEmpty('showroomZipCode') 
                    ? 'border-slate-300' 
                    : 'border-green-300'
                }`}
              />
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="showroomCountry" 
                className="text-slate-700 font-medium text-sm"
              >
                {t('form_step_1.showroom_country')}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <CountrySelector
                value={formData.showroomCountry}
                onChange={(code: string) => handleCountryChange(code)}
                placeholder={t('ui.country_selector.placeholder' as any)}
                className={`transition-all duration-200 ${
                  isFieldEmpty('showroomCountry') 
                    ? 'border-slate-300' 
                    : 'border-green-300'
                }`}
              />
            </div>
          </div>

          {formData.selectedRaynetAddress && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              Adresa doplněna z vybrané firmy v Raynetu ({formData.selectedRaynetAddress.name})
            </p>
          )}

          {isLoadingAddresses && (
            <p className="text-xs text-orange-600 flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              Načítám adresy firmy...
            </p>
          )}
        </div>

        {/* Visit Date */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label 
              htmlFor="visitDate" 
              className="text-slate-700 font-medium text-sm flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              {t('form_step_1.visit_date')}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="visitDate"
              type="date"
              value={formData.visitDate}
              onChange={(e) => handleChange('visitDate', e.target.value)}
              className={`h-12 transition-all duration-200 ${
                isFieldEmpty('visitDate') 
                  ? 'border-slate-300' 
                  : 'border-green-300'
              }`}
            />
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="mt-8 p-4 bg-slate-100 rounded-lg">
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-400"></div>
            {t('form_step_1.progress_indicator', { 
              current: requiredFields.filter(field => !isFieldEmpty(field)).length,
              total: requiredFields.length
            })}
          </span>
        </div>
      </div>

      {/* Address Selector Modal */}
      {showAddressSelector && (
        <AddressSelector
          addresses={companyAddresses}
          onSelect={handleAddressSelect}
          onCancel={handleAddressSelectorCancel}
        />
      )}
    </div>
  );
};

export default FormStepOne; 